import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';

import { Observable, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { catchError, map, take, tap } from 'rxjs/operators';

import { EntityExistsGuard } from '@qwyk/core';
import { CustomerUsersService } from '@qwyk/hub/administration/data-access/customer-users';
import { CustomerUsersFeature, CustomerUsersSelectors, CustomerUsersActions } from '@qwyk/hub/administration/data-access/customer-users';

@Injectable({
    providedIn: 'root',
})
export class CustomerUserExistsGuard extends EntityExistsGuard<string> {
    constructor(
        protected store: Store<CustomerUsersFeature.CustomerUsersPartialState>,
        protected service: CustomerUsersService,
        protected router: Router
    ) {
        super();
    }

    protected hasEntityInStore(id: string): Observable<boolean> {
        return this.store.pipe(
            select(CustomerUsersSelectors.getCustomerUsersEntities),
            map(entities => !!entities[id]),
            take(1)
        );
    }

    protected hasEntityInApi(
        id: string,
        next: ActivatedRouteSnapshot
    ): Observable<boolean> {
        return this.service.getCustomerUser(id).pipe(
            map(customerUser =>
                CustomerUsersActions.loadCustomerUserSuccess({ customerUser })
            ),
            tap(action => this.store.dispatch(action)),
            map(customerUser => !!customerUser),
            catchError(error => {
                this.router.navigate([`/error/${error.status}`], {
                    queryParams: {
                        source: next.pathFromRoot.map(p => p.url).join('/'),
                        errorName: error.statusText,
                    },
                });
                return of(false);
            })
        );
    }

    protected getEntityKey(next: ActivatedRouteSnapshot): string {
        return next.params['id'];
    }
}
